'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import Topbar from './Topbar';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import type { AppRole } from '@/lib/permissions';

interface AppLayoutProps {
  children: React.ReactNode;
  activeRoute: string;
  /** Optional: permission key required to view this page */
  requiredPermission?: keyof ReturnType<typeof getPermissions>;
}

export default function AppLayout({ children, activeRoute, requiredPermission }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const router = useRouter();
  const { user, loading, userProfile } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      router.replace('/login');
    }
  }, [loading, user, router]);

  // Route-level permission guard
  const perms = getPermissions(userProfile?.role as AppRole | undefined);
  const isAccessDenied = !loading && user && requiredPermission && !perms[requiredPermission];

  if (loading || (!user && !loading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAccessDenied) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-sm mx-auto px-6">
          <div className="w-16 h-16 rounded-2xl bg-danger/10 flex items-center justify-center mx-auto mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-danger">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h2 className="text-xl font-700 text-foreground mb-2">Akses Ditolak</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Anda tidak memiliki izin untuk mengakses halaman ini. Hubungi administrator untuk informasi lebih lanjut.
          </p>
          <button
            onClick={() => router.replace('/')}
            className="px-5 py-2.5 bg-primary text-white text-sm font-600 rounded-xl hover:bg-primary/90 transition-all"
          >
            Kembali ke Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#f4f6fb' }}>
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          activeRoute={activeRoute}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          sidebarCollapsed={sidebarCollapsed}
        />
        <main className="flex-1 overflow-y-auto scrollbar-thin">
          <div className="px-5 py-5 lg:px-7 lg:py-6 xl:px-8 max-w-screen-2xl mx-auto pb-24 lg:pb-6">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <div className="lg:hidden">
        <BottomNav activeRoute={activeRoute} />
      </div>
    </div>
  );
}